import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import Button from '../../components/ui/Button';
import {
  ChevronRight, ChevronLeft, Check, AlertTriangle, ChevronDown, ChevronUp,
  Calculator, Clock, Settings, ArrowLeft, AlertCircle,
} from 'lucide-react';

const ALL_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const DAY_LABELS: Record<string, string> = {
  MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun',
};

const LEVEL_GROUPS = [
  { label: 'Level 1-5', start: 1, end: 5 },
  { label: 'Level 6-10', start: 6, end: 10 },
  { label: 'Level 11-20', start: 11, end: 20 },
  { label: 'Level 21-30', start: 21, end: 30 },
];

const DEFAULT_LEVELS = [
  { level: 1, pct: 20 }, { level: 2, pct: 10 }, { level: 3, pct: 5 },
  { level: 4, pct: 5 }, { level: 5, pct: 4 }, { level: 6, pct: 3 },
  { level: 7, pct: 3 }, { level: 8, pct: 3 }, { level: 9, pct: 3 },
  { level: 10, pct: 3 }, { level: 11, pct: 2 }, { level: 12, pct: 2 },
  { level: 13, pct: 2 }, { level: 14, pct: 2 }, { level: 15, pct: 2 },
  { level: 16, pct: 2 }, { level: 17, pct: 2 }, { level: 18, pct: 2 },
  { level: 19, pct: 2 }, { level: 20, pct: 2 }, { level: 21, pct: 1 },
  { level: 22, pct: 1 }, { level: 23, pct: 1 }, { level: 24, pct: 1 },
  { level: 25, pct: 1 }, { level: 26, pct: 1 }, { level: 27, pct: 1 },
  { level: 28, pct: 1 }, { level: 29, pct: 1 }, { level: 30, pct: 1 },
];

interface WizardForm {
  name: string;
  description: string;
  fixedAmount: string;
  minAmount: string;
  maxAmount: string;
  dailyRate: string;
  durationMode: '200' | 'custom';
  customDuration: string;
  workingDays: string[];
  levelPoolAmount: string;
  levelPoolDuration: string;
  levels: { level: number; pct: number }[];
  maxEarningMultiple: string;
  totalCycles: string;
  compoundingEnabled: boolean;
  autoRenewal: boolean;
  withdrawalRestriction: string;
  isActive: boolean;
}

const emptyWizard: WizardForm = {
  name: '', description: '',
  fixedAmount: '', minAmount: '', maxAmount: '',
  dailyRate: '', durationMode: '200', customDuration: '',
  workingDays: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
  levelPoolAmount: '', levelPoolDuration: '200',
  levels: [...DEFAULT_LEVELS],
  maxEarningMultiple: '2.5', totalCycles: '1',
  compoundingEnabled: false, autoRenewal: false,
  withdrawalRestriction: '', isActive: true,
};

function planToWizard(plan: any): WizardForm {
  let levels = [...DEFAULT_LEVELS];
  if (plan.levelDistributionConfig) {
    try {
      const parsed = typeof plan.levelDistributionConfig === 'string'
        ? JSON.parse(plan.levelDistributionConfig) : plan.levelDistributionConfig;
      levels = Object.entries(parsed).map(([k, v]) => ({
        level: parseInt(k.replace('level', '')),
        pct: Math.round((v as number) * 100),
      })).filter(l => l.pct > 0).sort((a, b) => a.level - b.level);
      if (levels.length === 0) levels = [...DEFAULT_LEVELS];
    } catch { /* keep defaults */ }
  }
  const amt = parseFloat(plan.minAmount || '0');
  return {
    name: plan.name || '', description: plan.description || '',
    fixedAmount: String(amt || ''),
    minAmount: '', maxAmount: '',
    dailyRate: String(plan.dailyRate || ''),
    durationMode: plan.duration === 200 ? '200' : 'custom',
    customDuration: plan.duration === 200 ? '' : String(plan.duration || ''),
    workingDays: plan.workingDays ? plan.workingDays.split(',') : ['MON', 'TUE', 'WED', 'THU', 'FRI'],
    levelPoolAmount: String(plan.levelIncomePoolAmount || ''),
    levelPoolDuration: String(plan.levelIncomeDuration || '200'),
    levels,
    maxEarningMultiple: String(plan.maxEarningMultiple || '2.5'),
    totalCycles: String(plan.totalCycles || '1'),
    compoundingEnabled: plan.compoundingEnabled ?? false,
    autoRenewal: plan.autoRenewal ?? false,
    withdrawalRestriction: String(plan.withdrawalRestriction ?? ''),
    isActive: plan.isActive ?? true,
  };
}

function wizardToPayload(form: WizardForm) {
  const amt = parseFloat(form.fixedAmount) || 0;
  const duration = form.durationMode === '200' ? 200 : parseInt(form.customDuration) || 200;
  const levelConfig: Record<string, number> = {};
  form.levels.forEach(l => { if (l.pct > 0) levelConfig[`level${l.level}`] = l.pct / 100; });
  return {
    name: form.name, description: form.description,
    minAmount: amt, maxAmount: amt,
    dailyRate: parseFloat(form.dailyRate) || 0,
    maxEarningMultiple: parseFloat(form.maxEarningMultiple) || 2.5,
    duration, workingDays: form.workingDays.join(','),
    totalCycles: parseInt(form.totalCycles) || 1,
    levelIncomePoolAmount: parseFloat(form.levelPoolAmount) || 0,
    levelIncomeDuration: parseInt(form.levelPoolDuration) || 200,
    levelDistributionConfig: Object.keys(levelConfig).length > 0 ? JSON.stringify(levelConfig) : null,
    compoundingEnabled: form.compoundingEnabled,
    autoRenewal: form.autoRenewal,
    withdrawalRestriction: parseInt(form.withdrawalRestriction) || 0,
    isActive: form.isActive,
  };
}

function toggleDay(days: string[], day: string): string[] {
  if (days.includes(day)) return days.filter(d => d !== day);
  return [...days, day].sort((a, b) => ALL_DAYS.indexOf(a) - ALL_DAYS.indexOf(b));
}

const ic = 'w-full px-3 py-2.5 bg-white/[0.04] border border-white/[0.06] rounded-xl text-white text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-all';

/* ────── Step Indicator ────── */
function StepIndicator({ step, steps }: { step: number; steps: string[] }) {
  return (
    <div className="flex items-center gap-1 mb-8">
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <div className="flex items-center gap-1.5">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              i < step ? 'bg-emerald-500 text-white' : i === step ? 'bg-primary-600 text-white ring-2 ring-primary-400/30' : 'bg-white/[0.06] text-slate-500'
            }`}>
              {i < step ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-sm font-medium hidden sm:block ${i === step ? 'text-white' : 'text-slate-500'}`}>{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? 'bg-emerald-500' : 'bg-white/[0.06]'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ────── Step 1: Basic Plan ────── */
function Step1({ form, setForm }: { form: WizardForm; setForm: React.Dispatch<React.SetStateAction<WizardForm>> }) {
  const set = <K extends keyof WizardForm>(k: K, v: WizardForm[K]) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-1">Basic Plan</h2>
        <p className="text-sm text-slate-400">Set up the basic details of this investment plan.</p>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Plan Name *</label>
        <input value={form.name} onChange={e => set('name', e.target.value)} required className={ic} placeholder="e.g. Starter Plan" />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Description</label>
        <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} className={ic} placeholder="For members starting with a small investment." />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Plan Price (₹) *</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
          <input type="number" value={form.fixedAmount} onChange={e => set('fixedAmount', e.target.value)} className={ic + ' pl-7'} placeholder="e.g. 10000" />
        </div>
        <p className="text-xs text-slate-500 mt-1">Fixed investment amount for this plan</p>
      </div>
    </div>
  );
}

/* ────── Step 2: Daily Income ────── */
function Step2({ form, setForm }: { form: WizardForm; setForm: React.Dispatch<React.SetStateAction<WizardForm>> }) {
  const set = <K extends keyof WizardForm>(k: K, v: WizardForm[K]) => setForm(p => ({ ...p, [k]: v }));
  const minAmt = parseFloat(form.fixedAmount) || 0;
  const rate = parseFloat(form.dailyRate) || 0;
  const dailyIncome = minAmt * rate / 100;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-1">Daily Income</h2>
        <p className="text-sm text-slate-400">Set the daily income a member receives from this plan.</p>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Daily Income Rate (%)</label>
        <div className="relative">
          <input type="number" step="0.01" value={form.dailyRate} onChange={e => set('dailyRate', e.target.value)} className={ic + ' pr-8'} placeholder="e.g. 1" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
        </div>
        <p className="text-xs text-slate-500 mt-1">Enter the percentage, e.g. 1 for 1%</p>
      </div>
      {minAmt > 0 && rate > 0 && (
        <div className="p-5 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl text-center">
          <p className="text-xs text-emerald-300/70 uppercase tracking-wide mb-2">Member receives</p>
          <p className="text-4xl font-bold text-emerald-400">{formatCurrency(dailyIncome)}</p>
          <p className="text-sm text-emerald-300/70 mt-1">per working day</p>
          <p className="text-xs text-slate-500 mt-3">Based on {formatCurrency(minAmt)} × {rate}%</p>
        </div>
      )}
      <div className="border-t border-white/[0.06] pt-5">
        <label className="block text-xs font-medium text-slate-400 mb-2">How long should the plan run? *</label>
        <div className="flex gap-2 mb-3">
          {(['200', 'custom'] as const).map(mode => (
            <button key={mode} type="button" onClick={() => set('durationMode', mode)}
              className={`flex-1 py-3 rounded-xl text-sm font-medium border transition-all ${
                form.durationMode === mode ? 'bg-primary-600/15 border-primary-500/30 text-primary-400' : 'bg-white/[0.02] border-white/[0.06] text-slate-400 hover:text-white'
              }`}>
              {mode === '200' ? '200 Working Days' : 'Custom Duration'}
            </button>
          ))}
        </div>
        {form.durationMode === 'custom' && (
          <div>
            <label className="block text-xs text-slate-500 mb-1">Duration (working days)</label>
            <input type="number" value={form.customDuration} onChange={e => set('customDuration', e.target.value)} className={ic} placeholder="e.g. 150" />
          </div>
        )}
        <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" /> Only selected working days are counted. Weekends and holidays are skipped.
        </p>
      </div>
      <div className="border-t border-white/[0.06] pt-5">
        <label className="block text-xs font-medium text-slate-400 mb-2">Which days should income be paid?</label>
        <div className="flex gap-2">
          {ALL_DAYS.map(day => {
            const active = form.workingDays.includes(day);
            return (
              <button key={day} type="button" onClick={() => set('workingDays', toggleDay(form.workingDays, day))}
                className={`flex-1 py-3 rounded-xl text-sm font-medium border transition-all ${
                  active ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400' : 'bg-white/[0.02] border-white/[0.06] text-slate-500 hover:text-slate-300'
                }`}>
                {DAY_LABELS[day]}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-2">Income will be calculated only on selected working days.</p>
      </div>
    </div>
  );
}

/* ────── Step 3: Level Income ────── */
function Step3({ form, setForm }: { form: WizardForm; setForm: React.Dispatch<React.SetStateAction<WizardForm>> }) {
  const set = <K extends keyof WizardForm>(k: K, v: WizardForm[K]) => setForm(p => ({ ...p, [k]: v }));
  const pool = parseFloat(form.levelPoolAmount) || 0;
  const poolDur = parseInt(form.levelPoolDuration) || 200;
  const totalPct = form.levels.reduce((s, l) => s + l.pct, 0);
  const [expandedGroup, setExpandedGroup] = useState<number | null>(0);

  const updateLevelPct = (level: number, pct: number) => {
    set('levels', form.levels.map(l => l.level === level ? { ...l, pct: Math.max(0, Math.min(100, pct)) } : l));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-1">Level Income</h2>
        <p className="text-sm text-slate-400">Set the total amount available for your team-level income.</p>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Level Income Pool</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
          <input type="number" value={form.levelPoolAmount} onChange={e => set('levelPoolAmount', e.target.value)} className={ic + ' pl-7'} placeholder="e.g. 500" />
        </div>
        <p className="text-xs text-slate-500 mt-1">Total level income amount generated by one investment, distributed across eligible levels.</p>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Level Income Duration</label>
        <input type="number" value={form.levelPoolDuration} onChange={e => set('levelPoolDuration', e.target.value)} className={ic} placeholder="200" />
        <p className="text-xs text-slate-500 mt-1">Level income is distributed across {poolDur} eligible working days.</p>
      </div>
      <div className="border-t border-white/[0.06] pt-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold text-white">Level Distribution</p>
            <p className="text-xs text-slate-500 mt-0.5">Choose how the pool is distributed across levels.</p>
          </div>
          <div className={`px-3 py-1.5 rounded-lg text-xs font-bold ${
            totalPct === 100 ? 'bg-emerald-500/15 text-emerald-400' : totalPct > 100 ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'
          }`}>
            {totalPct === 100 ? <span className="flex items-center gap-1"><Check className="w-3 h-3" /> 100% Complete</span>
             : <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {totalPct}%</span>}
          </div>
        </div>
        {totalPct < 100 && <p className="text-xs text-amber-400/80 mb-3">⚠ {100 - totalPct}% of the pool is still unallocated.</p>}
        {totalPct > 100 && <p className="text-xs text-red-400/80 mb-3">⚠ Distribution exceeds 100%. Reduce some percentages.</p>}

        {LEVEL_GROUPS.map((group, gi) => (
          <div key={gi} className="mb-3">
            <button type="button" onClick={() => setExpandedGroup(expandedGroup === gi ? null : gi)}
              className="w-full flex items-center justify-between py-3 px-4 bg-white/[0.02] border border-white/[0.06] rounded-xl hover:bg-white/[0.04] transition-colors">
              <span className="text-sm font-semibold text-slate-300">{group.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">
                  {form.levels.filter(l => l.level >= group.start && l.level <= group.end && l.pct > 0).length} configured
                </span>
                {expandedGroup === gi ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </div>
            </button>
            {expandedGroup === gi && (
              <div className="mt-2 space-y-2 pl-2">
                {form.levels.filter(l => l.level >= group.start && l.level <= group.end).map(l => (
                  <div key={l.level} className="flex items-center gap-4 py-2.5 px-3 bg-white/[0.02] rounded-xl">
                    <span className="text-sm font-bold text-slate-400 w-10 shrink-0">L{l.level}</span>
                    <div className="flex-1 relative">
                      <input type="number" min={0} max={100} value={l.pct}
                        onChange={e => updateLevelPct(l.level, parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.06] rounded-lg text-white text-sm focus:border-primary-500 outline-none transition-all pr-8" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
                    </div>
                    {pool > 0 && l.pct > 0 && (
                      <div className="text-right shrink-0">
                        <p className="text-sm text-white font-medium">{formatCurrency(pool * l.pct / 100)}</p>
                        <p className="text-xs text-slate-500">{formatCurrency(pool * l.pct / 100 / poolDur)}/day</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────── Step 4: Review ────── */
function Step4({ form }: { form: WizardForm }) {
  const minAmt = parseFloat(form.fixedAmount) || 0;
  const rate = parseFloat(form.dailyRate) || 0;
  const dailyIncome = minAmt * rate / 100;
  const pool = parseFloat(form.levelPoolAmount) || 0;
  const poolDur = parseInt(form.levelPoolDuration) || 200;
  const duration = form.durationMode === '200' ? 200 : parseInt(form.customDuration) || 200;
  const totalPct = form.levels.reduce((s, l) => s + l.pct, 0);
  const activeLevels = form.levels.filter(l => l.pct > 0);
  const dayNames = form.workingDays.map(d => DAY_LABELS[d]).join(', ');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-1">Review & Create</h2>
        <p className="text-sm text-slate-400">Review your plan before creating it.</p>
      </div>
      <div className="bg-fintech-800/80 border border-white/[0.06] rounded-2xl p-6 space-y-5">
        <div className="text-center pb-4 border-b border-white/[0.06]">
          <p className="text-2xl font-bold text-white">{form.name || 'Untitled Plan'}</p>
          {form.description && <p className="text-sm text-slate-400 mt-1">{form.description}</p>}
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="p-4 bg-white/[0.02] rounded-xl"><p className="text-slate-400 text-xs mb-1">Investment</p><p className="font-semibold text-white text-lg">{formatCurrency(form.fixedAmount || '0')}</p></div>
          <div className="p-4 bg-white/[0.02] rounded-xl"><p className="text-slate-400 text-xs mb-1">Daily Income</p><p className="font-semibold text-emerald-400 text-lg">{rate}%</p></div>
          <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20"><p className="text-slate-400 text-xs mb-1">Daily Earnings</p><p className="font-semibold text-white text-lg">{formatCurrency(dailyIncome)} / day</p></div>
          <div className="p-4 bg-white/[0.02] rounded-xl"><p className="text-slate-400 text-xs mb-1">Duration</p><p className="font-semibold text-white text-lg">{duration} working days</p></div>
          <div className="p-4 bg-white/[0.02] rounded-xl col-span-2"><p className="text-slate-400 text-xs mb-1">Working Days</p><p className="font-semibold text-white">{dayNames}</p></div>
        </div>
        {pool > 0 && (
          <div className="border-t border-white/[0.06] pt-4">
            <p className="text-sm font-semibold text-white mb-3">Level Distribution</p>
            <div className="p-3 bg-primary-500/10 rounded-xl mb-3 flex justify-between text-sm">
              <span className="text-slate-400">Level Income Pool</span><span className="text-primary-400 font-semibold">{formatCurrency(pool)}</span>
            </div>
            <div className="space-y-1.5">
              {activeLevels.map(l => (
                <div key={l.level} className="flex items-center justify-between py-1.5 px-3 text-sm rounded-lg hover:bg-white/[0.02]">
                  <span className="text-slate-400">Level {l.level}</span>
                  <span className="text-slate-300">{l.pct}% → {formatCurrency(pool * l.pct / 100)} <span className="text-slate-500">({formatCurrency(pool * l.pct / 100 / poolDur)}/day)</span></span>
                </div>
              ))}
            </div>
            <div className={`mt-3 p-3 rounded-xl text-sm font-bold text-center ${
              totalPct === 100 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'
            }`}>
              {totalPct === 100 ? '✓ Distribution complete — 100% allocated' : `⚠ Distribution is ${totalPct}% — ${100 - totalPct}% unallocated`}
            </div>
          </div>
        )}
      </div>
      <AdvancedReview form={form} />
    </div>
  );
}

/* ────── Advanced Settings Review ────── */
function AdvancedReview({ form }: { form: WizardForm }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/[0.06] rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-300">Advanced Settings</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="p-4 space-y-3 text-sm border-t border-white/[0.06]">
          <div className="flex justify-between"><span className="text-slate-400">Maximum Return</span><span className="text-white">{form.maxEarningMultiple}x</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Total Cycles</span><span className="text-white">{form.totalCycles}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Compounding</span><span className="text-white">{form.compoundingEnabled ? 'Enabled' : 'Disabled'}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Auto Renewal</span><span className="text-white">{form.autoRenewal ? 'On' : 'Off'}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Withdrawal Restriction</span><span className="text-white">{form.withdrawalRestriction ? `${form.withdrawalRestriction} days` : 'None'}</span></div>
          <div className="flex justify-between items-center"><span className="text-slate-400">Status</span>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${form.isActive ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/[0.06] text-slate-400'}`}>{form.isActive ? 'Active' : 'Draft'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ────── Advanced Settings Edit ────── */
function AdvancedEdit({ form, setForm }: { form: WizardForm; setForm: React.Dispatch<React.SetStateAction<WizardForm>> }) {
  const set = <K extends keyof WizardForm>(k: K, v: WizardForm[K]) => setForm(p => ({ ...p, [k]: v }));
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/[0.06] rounded-xl overflow-hidden mt-6">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-300">Advanced Settings</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="p-5 space-y-5 border-t border-white/[0.06]">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Maximum Return</label>
            <div className="relative">
              <input type="number" step="0.1" value={form.maxEarningMultiple} onChange={e => set('maxEarningMultiple', e.target.value)} className={ic + ' pr-8'} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">x</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Maximum amount a member can receive relative to their investment.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Total Cycles</label>
            <input type="number" value={form.totalCycles} onChange={e => set('totalCycles', e.target.value)} className={ic} />
            <p className="text-xs text-slate-500 mt-1">Number of times the plan cycle repeats.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-2">Compounding</label>
            <div className="flex gap-2">
              {([false, true] as const).map(v => (
                <button key={String(v)} type="button" onClick={() => set('compoundingEnabled', v)}
                  className={`flex-1 py-3 rounded-xl text-sm font-medium border transition-all ${
                    form.compoundingEnabled === v ? 'bg-primary-600/15 border-primary-500/30 text-primary-400' : 'bg-white/[0.02] border-white/[0.06] text-slate-400 hover:text-white'
                  }`}>
                  {v ? 'Enabled' : 'Disabled'}
                </button>
              ))}
            </div>
            {form.compoundingEnabled && <p className="text-xs text-slate-500 mt-1.5">Allow members to reinvest eligible earnings into a new cycle.</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-2">Automatic Renewal</label>
            <div className="flex gap-2">
              {([false, true] as const).map(v => (
                <button key={String(v)} type="button" onClick={() => set('autoRenewal', v)}
                  className={`flex-1 py-3 rounded-xl text-sm font-medium border transition-all ${
                    form.autoRenewal === v ? 'bg-primary-600/15 border-primary-500/30 text-primary-400' : 'bg-white/[0.02] border-white/[0.06] text-slate-400 hover:text-white'
                  }`}>
                  {v ? 'On' : 'Off'}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1.5">Automatically start a new cycle after completion.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Withdrawal Restriction</label>
            <input type="number" value={form.withdrawalRestriction} onChange={e => set('withdrawalRestriction', e.target.value)} className={ic} placeholder="0" />
            <p className="text-xs text-slate-500 mt-1">{form.withdrawalRestriction ? `Withdrawal available after ${form.withdrawalRestriction} working days.` : 'No restriction'}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-2">Plan Status</label>
            <div className="flex gap-2">
              {([true, false] as const).map(v => (
                <button key={String(v)} type="button" onClick={() => set('isActive', v)}
                  className={`flex-1 py-3 rounded-xl text-sm font-medium border transition-all flex items-center justify-center gap-2 ${
                    form.isActive === v ? (v ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400' : 'bg-white/[0.06] border-white/[0.12] text-slate-300') : 'bg-white/[0.02] border-white/[0.06] text-slate-400 hover:text-white'
                  }`}>
                  <span className={`w-2 h-2 rounded-full ${v ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                  {v ? 'Active' : 'Draft'}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1.5">{form.isActive ? 'Plan will be visible to eligible members.' : 'Plan will not be available to members.'}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ────── Main Page Component ────── */
export default function AdminPlanWizardPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<WizardForm>(emptyWizard);
  const [error, setError] = useState('');

  const { data: existingPlan, isLoading: loadingPlan } = useQuery({
    queryKey: ['adminPlan', id],
    queryFn: () => api.get('/investments/plans').then(r => r.data.find((p: any) => p.id === id)),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existingPlan && isEdit) setForm(planToWizard(existingPlan));
  }, [existingPlan, isEdit]);

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/admin/plans', data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminPlans'] }); navigate('/admin/plans'); },
    onError: (err: any) => {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Failed to create plan. Please check all fields.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.patch(`/admin/plans/${id}`, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminPlans'] }); navigate('/admin/plans'); },
    onError: (err: any) => {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Failed to update plan. Please check all fields.');
    },
  });

  const canNext = () => {
    if (step === 0) return form.name.trim() !== '' && parseFloat(form.fixedAmount) > 0;
    if (step === 1) return parseFloat(form.dailyRate) > 0;
    return true;
  };

  const handleSubmit = () => {
    setError('');
    const payload = wizardToPayload(form);
    if (isEdit && id) updateMutation.mutate({ id, data: payload });
    else createMutation.mutate(payload);
  };

  const steps = ['Basic', 'Daily Income', 'Level Income', 'Review'];

  if (isEdit && loadingPlan) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/plans')} className="p-2 rounded-xl bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors">
          <ArrowLeft className="w-4 h-4 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">{isEdit ? 'Edit Investment Plan' : 'Create Investment Plan'}</h1>
          <p className="text-sm text-slate-400">{isEdit ? 'Update the plan configuration' : 'Set up a new investment plan for your members'}</p>
        </div>
      </div>

      <StepIndicator step={step} steps={steps} />

      {/* Step Content */}
      <div className="bg-fintech-800/50 border border-white/[0.06] rounded-2xl p-6 lg:p-8">
        {step === 0 && <Step1 form={form} setForm={setForm} />}
        {step === 1 && <Step2 form={form} setForm={setForm} />}
        {step === 2 && <Step3 form={form} setForm={setForm} />}
        {step === 3 && <Step4 form={form} />}
        {step < 3 && <AdvancedEdit form={form} setForm={setForm} />}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2.5 p-3.5 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">✕</button>
        </div>
      )}

      {/* Navigation */}
      <div className="flex gap-3">
        {step > 0 && (
          <Button type="button" variant="secondary" className="flex-1" onClick={() => setStep(s => s - 1)}>
            <ChevronLeft className="w-4 h-4 mr-1" />Back
          </Button>
        )}
        {step < 3 ? (
          <Button type="button" variant="primary" className="flex-1" disabled={!canNext()} onClick={() => setStep(s => s + 1)}>
            Continue<ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button type="button" variant="primary" className="flex-1" isLoading={createMutation.isPending || updateMutation.isPending} onClick={handleSubmit}>
            <Check className="w-4 h-4 mr-1" />{isEdit ? 'Update Plan' : 'Create Plan'}
          </Button>
        )}
      </div>
    </div>
  );
}
